export type GtmEvent = {
  event?: string;
  'gtm.element.dataset.gtmComponentName'?: string;
  'gtm.element.dataset.gtmDatasourceId'?: string;
  'gtm.element.dataset.gtmDatasourceName'?: string;
  'gtm.element.dataset.gtmLinkName'?: string;
  'gtm.element.dataset.gtmLinkUrl'?: string;

  // Form submission specific fields
  enquiry_type?: string;
  form_value?: string;
  infinity_id?: string;
  infinity_number?: string;
  form_name?: string;
  content_group?: string;
  content_group2?: string;

  // Navigation specific fields
  click_text?: string;
  menu_cat1?: string;
  menu_cat2?: string;
  menu_cat3?: string;
  /** 1 = main nav, 2 = side nav, 3 = sub nav. Numeric, per the analytics spec. */
  nav_level?: number;
  /** Where the click happened: `header`, `mega_menu`, `mobile_menu`, `breadcrumbs`. */
  nav_location?: string;
  link_url?: string;
  /** Full trail, e.g. `Home > Personal > Conveyancing & Property`. Breadcrumbs only. */
  breadcrumb_path?: string;

  // Content view specific fields
  content_type?: string;

  [name: string]: string | number | undefined;
};

const HOMEPAGE_GROUP = 'homepage';

export const getContentGroups = (
  pathname?: string,
  languageCodes?: string[]
): { content_group: string; content_group2: string } => {
  // Auto-detect pathname if not provided
  const currentPathname =
    pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Check if pathname is empty or just root
  if (!currentPathname || currentPathname === '/') {
    return {
      content_group: HOMEPAGE_GROUP,
      content_group2: HOMEPAGE_GROUP,
    };
  }

  // Split pathname and filter out empty strings
  const segments = currentPathname.split('/').filter(Boolean);

  // Fallback to common languages if not provided (for safety)
  const defaultLanguages = ['en', 'es', 'fr', 'de', 'ar'];
  const languagesToFilter =
    languageCodes && languageCodes.length > 0 ? languageCodes : defaultLanguages;

  // Check if pathname is just a language code (e.g., "/en" or "/en/")
  // After filtering empty strings, if we only have 1 segment and it's a language code, it's homepage
  if (
    segments.length === 1 &&
    languagesToFilter.some((lang) => lang.toLowerCase() === segments[0].toLowerCase())
  ) {
    return {
      content_group: HOMEPAGE_GROUP,
      content_group2: HOMEPAGE_GROUP,
    };
  }

  // Filter out language codes from segments
  const contentSegments = segments.filter(
    (segment) => !languagesToFilter.some((lang) => lang.toLowerCase() === segment.toLowerCase())
  );

  // Authored paths are lower case already; normalising anyway so one odd segment cannot
  // create a second bucket for a page.
  const content_group = (contentSegments[0] || HOMEPAGE_GROUP).toLowerCase();
  const content_group2 = (contentSegments[1] || '').toLowerCase();

  return {
    content_group,
    content_group2,
  };
};

// Helper function for Content GTM events

export const createCTAClickGTMEvent = (options: {
  ctaText?: string;
  ctaType?: 'blue' | 'clear' | 'text link';
  event?: string;
  header?: string;
  componentName?: string;
  additionalData?: Record<string, string | number | boolean>;
  ctaLink?: string;
}): GtmEvent => {
  const contentGroups = getContentGroups();

  const eventName = options.event
    ? options.event
    : options.ctaLink && options.ctaLink.indexOf('tel:') > -1
      ? 'call_to_click'
      : 'cta_click';
  return {
    event: eventName,
    component_header: options.header || '',
    component_name: options.componentName || '',
    click_text: options.ctaText || '',
    cta_type: options.ctaType || '',
    content_group: contentGroups.content_group,
    content_group2: contentGroups.content_group2,
    ...options.additionalData,
  };
};

export const createTabClickGTMEvent = (options: {
  event?: string;
  header?: string;
  componentName?: string;
  ctaText?: string;
}): GtmEvent => {
  const contentGroups = getContentGroups();

  return {
    event: options.event || 'tab_click',
    component_header: options.header || '',
    component_name: options.componentName || '',
    click_text: options.ctaText || '',
    content_group: contentGroups.content_group,
    content_group2: contentGroups.content_group2,
  };
};

/** Lower-cased for analytics grouping; see `createNavClickGTMEvent`. */
const toAnalyticsText = (value?: string): string => (value ?? '').toLowerCase();
export const createNavClickGTMEvent = (options: {
  clickText?: string;
  menuCat1?: string; // Tier 1 navigation title
  menuCat2?: string; // Tier 2 category title
  menuCat3?: string; // Tier 3 link title
  navLevel?: number;
  navLocation?: string;
  linkUrl?: string;
  breadcrumbPath?: string;
  event?: string;
}): GtmEvent => {
  const contentGroups = getContentGroups();

  const gtmEvent: GtmEvent = {
    event: options.event || 'nav_click',
    click_text: toAnalyticsText(options.clickText),
    menu_cat1: toAnalyticsText(options.menuCat1),
    menu_cat2: toAnalyticsText(options.menuCat2),
    menu_cat3: toAnalyticsText(options.menuCat3),
    content_group: contentGroups.content_group,
    content_group2: contentGroups.content_group2,
    link_url: options.linkUrl || '',
  };

  // Only present where they mean something: breadcrumbs carry no level, menus carry no trail.
  if (options.navLevel !== undefined) {
    gtmEvent.nav_level = options.navLevel;
  }
  if (options.navLocation) {
    gtmEvent.nav_location = options.navLocation;
  }
  if (options.breadcrumbPath) {
    // The trail is text like the rest of the event, so it is grouped the same way.
    gtmEvent.breadcrumb_path = toAnalyticsText(options.breadcrumbPath);
  }

  return gtmEvent;
};

/**
 * Mobile menu opening and closing. `navOpen` false emits `nav_closed`.
 */
export const createNavToggleGTMEvent = (options: {
  navOpen: boolean;
  navLocation?: string;
}): GtmEvent => ({
  event: options.navOpen ? 'nav_open' : 'nav_closed',
  nav_location: options.navLocation || 'mobile_menu',
});

// Helper function for Content View GTM events
export const createContentViewGTMEvent = (options: {
  contentType?: string;
  event?: string;
}): GtmEvent => {
  const contentGroups = getContentGroups();

  return {
    event: options.event || 'content_view',
    content_type: options.contentType || '',
    content_group: contentGroups.content_group,
    content_group2: contentGroups.content_group2,
  };
};

// Helper function for Tool Start GTM events
export const toolStartFinishGTMEvent = (options: {
  toolName?: string;
  event?: string;
}): GtmEvent => {
  return {
    event: options.event || 'tool_start' || 'tool_submit',
    tool_name: options.toolName || '',
  };
};

// Helper function for Tool Start GTM events
export const toolSectionCompleteGTMEvent = (options: {
  toolName?: string;
  sectionName?: string;
  event?: string;
}): GtmEvent => {
  return {
    event: options.event || 'tool_section_complete',
    tool_name: options.toolName || '',
    tool_section: options.sectionName || '',
  };
};

// Helper function for wills page_view GTM event
export const willsPageViewGTMEvent = (options: {
  pagePath?: string;
  toolSection?: string;
  event?: string;
}): GtmEvent => {
  return {
    event: options.event || 'page_view',
    page_path: options.pagePath || '',
    tool_section: options.toolSection || '',
  };
};

export type VideoInteractionType = 'impression' | 'play' | 'pause' | 'complete' | 'progress';

// Helper function for video engagement GTM events
export const createVideoInteractionGTMEvent = (options: {
  videoTitle?: string;
  videoId?: string;
  pageUrl?: string;
  interactionType: VideoInteractionType;
  timestamp?: string;
  /** Milestone percentage reached (progress events only), e.g. 25, 50, 75, 100 */
  videoPercent?: number;
  event?: string;
}): GtmEvent => {
  const pageUrl =
    options.pageUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const timestamp = options.timestamp || new Date().toISOString();

  return {
    event: options.event || 'video_interaction',
    video_title: options.videoTitle || '',
    video_id: options.videoId || '',
    page_url: pageUrl,
    interaction_type: options.interactionType,
    timestamp,
    ...(options.interactionType === 'progress' && options.videoPercent != null
      ? { video_percent: String(options.videoPercent) }
      : {}),
  };
};
